import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Apple logo as inline SVG for reliability
const AppleLogo = () => (
  <svg viewBox="0 0 384 512" className="h-12 w-auto fill-current">
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
  </svg>
);

const brands = [
  {
    name: 'Apple',
    Logo: AppleLogo,
    logo: null,
    description: 'iPhone, iPad, MacBook, Apple Watch servis ve onarım hizmetleri',
    href: '/blog/apple',
    bgColor: 'bg-gradient-to-br from-gray-50 to-gray-100',
  },
  {
    name: 'Samsung',
    Logo: null,
    logo: 'https://images.samsung.com/is/image/samsung/assets/global/about-us/brand/logo/256_144_3.png?$512_N_PNG$',
    description: 'Galaxy serisi telefon, tablet ve akıllı saat tamiri',
    href: '/blog/samsung',
    bgColor: 'bg-gradient-to-br from-blue-50 to-blue-100',
  },
  {
    name: 'Xiaomi',
    Logo: null,
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/a/ae/Xiaomi_logo_%282021-%29.svg/2048px-Xiaomi_logo_%282021-%29.svg.png',
    description: 'Xiaomi ve Redmi serisi cihazlar için uzman servis',
    href: '/blog/xiaomi',
    bgColor: 'bg-gradient-to-br from-orange-50 to-orange-100',
  },
];

export const BrandShowcase = () => {
  return (
    <section className="py-16 lg:py-24 bg-muted/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 lg:mb-16">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block text-sm font-medium text-primary uppercase tracking-wider mb-3"
          >
            Yetkili Servis
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-3xl sm:text-4xl lg:text-5xl font-bold font-display text-foreground mb-4"
          >
            Desteklediğimiz Markalar
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted-foreground max-w-2xl mx-auto"
          >
            Dünya'nın önde gelen teknoloji markalarının cihazlarına profesyonel servis hizmeti veriyoruz.
          </motion.p>
        </div>

        {/* Brand Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {brands.map((brand, index) => (
            <motion.div
              key={brand.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <Link
                to={brand.href}
                className={`group block ${brand.bgColor} rounded-2xl p-8 h-full hover:shadow-lg transition-all duration-300`}
              >
                <div className="h-16 mb-6 flex items-center text-gray-800 group-hover:text-primary transition-colors">
                  {brand.Logo ? (
                    <brand.Logo />
                  ) : (
                    <img
                      src={brand.logo}
                      alt={brand.name}
                      className="h-full w-auto max-w-[150px] object-contain"
                    />
                  )}
                </div>
                <h3 className="text-xl font-semibold font-display text-foreground mb-2">
                  {brand.name} Servisi
                </h3>
                <p className="text-muted-foreground mb-4">{brand.description}</p>
                <div className="flex items-center text-primary font-medium">
                  <span className="text-sm">Cihazları Görüntüle</span>
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <Button asChild size="lg">
            <Link to="/servis">
              Hemen Servis Al
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default BrandShowcase;
